import React, { useEffect, useMemo, useState, useRef } from 'react';
import { CalculatorInputs } from '../components/CalculatorInputs';
import { AssumptionsSidebar } from '../components/AssumptionsSidebar';
import { ResultsSummary } from '../components/ResultsSummary';
import { ProfitChart } from '../components/ProfitChart';
import { ProjectionTable } from '../components/ProjectionTable';
import { HiringFunnelModal } from '../components/HiringFunnelModal';
import { ExpandableHiringFunnel } from '../components/ExpandableHiringFunnel';
import { Inputs, DEFAULT_INPUTS, calculateModel, PackageType, BuildPayType, IndustryType, optimizeCampaignsForRevenueTarget, optimizeCampaignsForTeamTarget } from '../utils/roiCalculations';
import { fmtMoney } from '../utils/formatters';
const CORRECT_PASSWORD = 'HIRE100k';
const ADDITIONAL_PASSWORDS = ['mymanny', '1234', 'temp1234'];
const TEMP_PASSWORD = 'UNTD.solar';
const TEMP_PASSWORD_START = new Date('2026-02-02T00:00:00');
const TEMP_PASSWORD_END = new Date('2026-02-07T00:00:00'); // End of Feb 6th (midnight Feb 7th)
const TEMP_PASSWORD_2 = 'Polaris';
const TEMP_PASSWORD_2_START = new Date('2026-03-27T00:00:00');
const TEMP_PASSWORD_2_END = new Date('2026-03-30T00:00:00'); // End of Mar 29th (midnight Mar 30th)
const TEMP_PASSWORD_3 = '1234';
const TEMP_PASSWORD_3_START = new Date('2026-04-07T00:00:00');
const TEMP_PASSWORD_3_END = new Date('2026-04-23T00:00:00'); // End of Apr 22nd (midnight Apr 23rd)
const AUTH_KEY = 'hire_calculator_authenticated';
const CAMPAIGN_SPEND_STEP = 2500;
// Ad cost per hire adjusts in $50 increments.
const CPH_STEP = 50;
// Inline numeric fields size themselves to their contents.
const fieldWidthCh = (value: string | number) => Math.max(2, String(value).length + 0.5);
// Expected hires are shown as a range using a ±$150 band around the median
// ad spend cost per hire. The median itself drives the projection table.
const CPH_RANGE_SPREAD = 150;
const isValidPassword = (password: string): boolean => {
  // Check permanent passwords
  if (password === CORRECT_PASSWORD) {
    return true;
  }
  // Check additional permanent passwords
  if (ADDITIONAL_PASSWORDS.includes(password)) {
    return true;
  }
  // Check temporary passwords within date range
  const now = new Date();
  if (password === TEMP_PASSWORD && now >= TEMP_PASSWORD_START && now < TEMP_PASSWORD_END) {
    return true;
  }
  if (password === TEMP_PASSWORD_2 && now >= TEMP_PASSWORD_2_START && now < TEMP_PASSWORD_2_END) {
    return true;
  }
  if (password === TEMP_PASSWORD_3 && now >= TEMP_PASSWORD_3_START && now < TEMP_PASSWORD_3_END) {
    return true;
  }
  return false;
};
export function ROICalculator() {
  // Set browser tab title
  useEffect(() => {
    document.title = 'Recruiting ROI Calculator';
  }, []);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [isLightMode, setIsLightMode] = useState(true);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [inputs, setInputs] = useState<Inputs>(DEFAULT_INPUTS);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);
  // Sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  // Shared state for toggles
  const [halfSales, setHalfSales] = useState(false);
  const [doubleAttrition, setDoubleAttrition] = useState(false);
  const [onTarget, setOnTarget] = useState(false);
  const [preOnTargetValues, setPreOnTargetValues] = useState<{
    pctLaunched: number;
    pct30: number;
    pct90: number;
    pct1y: number;
    salesFirst90Total: number;
    salesVetMonthly: number;
  } | null>(null);
  const [plusFive, setPlusFive] = useState(false);
  const [prePlusFiveValues, setPrePlusFiveValues] = useState<{
    pctLaunched: number;
    pct30: number;
    pct90: number;
    pct1y: number;
    salesFirst90Total: number;
    salesVetMonthly: number;
  } | null>(null);
  const [packageScenario, setPackageScenario] = useState<'doubleAttrition' | 'plusFive' | null>(null);
  const previousPackageRef = useRef<PackageType | null>(null);
  const [cphLowDraft, setCphLowDraft] = useState<string | null>(null);
  const [cphHighDraft, setCphHighDraft] = useState<string | null>(null);
  const [preOptimizeMonthlyCampaigns, setPreOptimizeMonthlyCampaigns] = useState<number[] | null>(null);
  const [timeframe, setTimeframe] = useState<3 | 6 | 12>(6);
  const [savePdfLabel, setSavePdfLabel] = useState('Save as PDF');
  const [showPackageTooltip, setShowPackageTooltip] = useState(false);
  const [showBuildTooltip, setShowBuildTooltip] = useState(false);
  const [expandedReviewSections, setExpandedReviewSections] = useState({
    campaignAdSpend: false,
    revenueMargins: false,
    salesPerformance: false,
    retentionModeling: false
  });
  const [showHiringFunnel, setShowHiringFunnel] = useState(false);
  const [showHiringMetrics, setShowHiringMetrics] = useState(false);
  const [showOverhead, setShowOverhead] = useState(false);
  // Overhead values state
  const [preInterviewHours, setPreInterviewHours] = useState(40);
  const [interviewToOnboardingHours, setInterviewToOnboardingHours] = useState(60);
  const [adminCost, setAdminCost] = useState(2000);
  const [leadershipCost, setLeadershipCost] = useState(3000);
  const [reviewSpendInput, setReviewSpendInput] = useState('');
  const resultsRef = useRef<HTMLDivElement>(null);
  const reviewRef = useRef<HTMLDivElement>(null);
  // Check authentication on mount (client-side only)
  useEffect(() => {
    setIsClient(true);
    const authStatus = localStorage.getItem(AUTH_KEY);
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);
  // Load state from URL on mount
  useEffect(() => {
    if (!isClient) return;
    const params = new URLSearchParams(window.location.search);
    if ([...params.keys()].length === 0) return;
    const newInputs = {
      ...DEFAULT_INPUTS
    };
    const parseNum = (key: string, def: number) => {
      const val = params.get(key);
      return val ? parseFloat(val) : def;
    };
    if (params.has('pkg')) newInputs.pkg = params.get('pkg') as PackageType;
    if (params.has('buildPay')) newInputs.buildPay = params.get('buildPay') as BuildPayType;
    if (params.has('industry')) newInputs.industry = params.get('industry') as IndustryType;
    if (params.has('salesSchoolEnabled')) {
      newInputs.salesSchoolEnabled = params.get('salesSchoolEnabled') !== 'false';
    }
    newInputs.campaigns = parseNum('campaigns', DEFAULT_INPUTS.campaigns);
    newInputs.adSpend = parseNum('adSpend', DEFAULT_INPUTS.adSpend);
    newInputs.cph = parseNum('cph', DEFAULT_INPUTS.cph);
    newInputs.ticket = parseNum('ticket', DEFAULT_INPUTS.ticket);
    newInputs.gpm = parseNum('gpm', DEFAULT_INPUTS.gpm);
    newInputs.salesVetMonthly = parseNum('salesVetMonthly', DEFAULT_INPUTS.salesVetMonthly);
    newInputs.salesFirst90Total = parseNum('salesFirst90Total', DEFAULT_INPUTS.salesFirst90Total);
    newInputs.pctLaunched = parseNum('pctLaunched', DEFAULT_INPUTS.pctLaunched);
    newInputs.pct30 = parseNum('pct30', DEFAULT_INPUTS.pct30);
    newInputs.pct90 = parseNum('pct90', DEFAULT_INPUTS.pct90);
    newInputs.pct1y = parseNum('pct1y', DEFAULT_INPUTS.pct1y);
    newInputs.pullthrough = parseNum('pullthrough', DEFAULT_INPUTS.pullthrough);
    // Initialize monthly campaigns based on base campaigns if not present
    newInputs.monthlyCampaigns = Array(12).fill(newInputs.campaigns);
    newInputs.monthlyAdSpend = Array(12).fill(newInputs.adSpend);
    setInputs(newInputs);
    if (params.has('calculated') && params.get('calculated') === 'true') {
      setHasCalculated(true);
    }
  }, [isClient]);
  // Update URL when inputs change
  useEffect(() => {
    if (!isClient) return;
    const params = new URLSearchParams();
    Object.entries(inputs).forEach(([key, value]) => {
      if (key !== 'monthlyCampaigns' && key !== 'monthlyAdSpend') {
        // Don't clutter URL with arrays
        params.set(key, String(value));
      }
    });
    if (hasCalculated) {
      params.set('calculated', 'true');
    }
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
  }, [inputs, hasCalculated, isClient]);
  // Sync monthly campaigns when base campaigns change - ONE WAY ONLY
  useEffect(() => {
    // When base campaigns changes, ALWAYS update all monthly campaigns to match
    // This is a one-way sync: top → table only
    if (inputs.campaigns !== undefined) {
      // Only update if monthlyCampaigns doesn't exist or if all values match the old campaigns value
      // This allows manual edits in the table to persist
      const needsInit = !inputs.monthlyCampaigns || inputs.monthlyCampaigns.length !== 12;
      if (needsInit) {
        setInputs(prev => ({
          ...prev,
          monthlyCampaigns: Array(12).fill(inputs.campaigns)
        }));
      } else {
        // Check if all monthly values are the same (no manual edits)
        const allSame = inputs.monthlyCampaigns.every((c, i, arr) => c === arr[0]);
        // If all values are the same AND different from current campaigns input,
        // update them all to match the new campaigns value
        if (allSame && inputs.monthlyCampaigns[0] !== inputs.campaigns) {
          setInputs(prev => ({
            ...prev,
            monthlyCampaigns: Array(12).fill(inputs.campaigns)
          }));
        }
      }
    }
  }, [inputs.campaigns]);
  // Sync monthly ad spend when base ad spend changes - ONE WAY ONLY
  useEffect(() => {
    if (inputs.adSpend !== undefined) {
      const needsInit = !inputs.monthlyAdSpend || inputs.monthlyAdSpend.length !== 12;
      if (needsInit) {
        setInputs(prev => ({
          ...prev,
          monthlyAdSpend: Array(12).fill(inputs.adSpend)
        }));
      } else {
        // Check if all monthly values are the same (no manual edits)
        const allSame = inputs.monthlyAdSpend.every((c, i, arr) => c === arr[0]);
        // If all values are the same AND different from current ad spend input,
        // update them all to match the new ad spend value
        if (allSame && inputs.monthlyAdSpend[0] !== inputs.adSpend) {
          setInputs(prev => ({
            ...prev,
            monthlyAdSpend: Array(12).fill(inputs.adSpend)
          }));
        }
      }
    }
  }, [inputs.adSpend]);
  // Initialize if missing
  if (!inputs.monthlyCampaigns || inputs.monthlyCampaigns.length !== 12) {
    setInputs(prev => ({
      ...prev,
      monthlyCampaigns: Array(12).fill(prev.campaigns)
    }));
  }
  // Calculate results - recalculates whenever inputs change (including monthlyCampaigns and monthlyAdSpend)
  const results = useMemo(() => {
    // Set default buildPay if not selected
    const inputsWithDefaults = {
      ...inputs,
      pkg: inputs.pkg,
      buildPay: inputs.buildPay || 'upfront'
    };
    return calculateModel(inputsWithDefaults);
  }, [inputs, inputs.monthlyCampaigns, inputs.monthlyAdSpend, inputs.pkg, inputs.buildPay]);
  const toggleReviewSection = (section: keyof typeof expandedReviewSections) => {
    setExpandedReviewSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValidPassword(password)) {
      setIsAuthenticated(true);
      localStorage.setItem(AUTH_KEY, 'true');
      setError('');
    } else {
      setError('Incorrect access code. Please try again.');
      setPassword('');
    }
  };
  const handleCalculate = () => {
    // Ensure monthly campaigns are initialized
    if (!inputs.monthlyCampaigns) {
      setInputs(prev => ({
        ...prev,
        monthlyCampaigns: Array(12).fill(prev.campaigns)
      }));
    }
    setHasCalculated(true);
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };
  const handleReview = () => {
    setHasReviewed(true);
    setTimeout(() => {
      reviewRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };
  const handleReset = () => {
    setInputs(DEFAULT_INPUTS);
    setHasCalculated(false);
    setHasReviewed(false);
    setHalfSales(false);
    setDoubleAttrition(false);
    setOnTarget(false);
    setPreOnTargetValues(null);
    setPlusFive(false);
    setPrePlusFiveValues(null);
    setPackageScenario(null);
    previousPackageRef.current = DEFAULT_INPUTS.pkg;
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  // Scenarios applied on behalf of a package are cleared when the package
  // changes. Manual scenario changes clear this marker and remain untouched.
  useEffect(() => {
    const previousPackage = previousPackageRef.current;
    if (previousPackage === inputs.pkg) return;
    if (packageScenario === 'doubleAttrition') {
      setDoubleAttrition(false);
    }
    if (packageScenario === 'plusFive') {
      if (prePlusFiveValues) {
        setInputs(prev => ({
          ...prev,
          ...prePlusFiveValues
        }));
      }
      setPlusFive(false);
      setPrePlusFiveValues(null);
    }
    setPackageScenario(null);
    previousPackageRef.current = inputs.pkg;
  }, [inputs.pkg]);
  const handleDoubleAttritionChange = (value: boolean) => {
    setPackageScenario(scenario => scenario === 'doubleAttrition' ? null : scenario);
    setDoubleAttrition(value);
  };
  const handleToggleOnTarget = () => {
    if (!onTarget) {
      // Save current values before overriding
      setPreOnTargetValues({
        pctLaunched: inputs.pctLaunched as number,
        pct30: inputs.pct30 as number,
        pct90: inputs.pct90 as number,
        pct1y: inputs.pct1y as number,
        salesFirst90Total: inputs.salesFirst90Total as number,
        salesVetMonthly: inputs.salesVetMonthly as number
      });
      // Apply "On Target" values
      setInputs(prev => ({
        ...prev,
        pctLaunched: 80,
        pct30: 40,
        pct90: 80,
        pct1y: 40,
        salesFirst90Total: Math.round((prev.salesFirst90Total as number) * 1.1 * 10) / 10,
        salesVetMonthly: Math.round((prev.salesVetMonthly as number) * 1.1 * 10) / 10
      }));
      setOnTarget(true);
    } else {
      // Restore saved values
      if (preOnTargetValues) {
        setInputs(prev => ({
          ...prev,
          ...preOnTargetValues
        }));
      }
      setOnTarget(false);
      setPreOnTargetValues(null);
    }
  };
  const handlePlusFive = () => {
    setPackageScenario(scenario => scenario === 'plusFive' ? null : scenario);
    if (!plusFive) {
      // Save current values before applying +5%
      setPrePlusFiveValues({
        pctLaunched: inputs.pctLaunched as number,
        pct30: inputs.pct30 as number,
        pct90: inputs.pct90 as number,
        pct1y: inputs.pct1y as number,
        salesFirst90Total: inputs.salesFirst90Total as number,
        salesVetMonthly: inputs.salesVetMonthly as number
      });
      setInputs(prev => ({
        ...prev,
        pctLaunched: Math.min(100, (prev.pctLaunched as number) + 5),
        pct30: Math.min(100, (prev.pct30 as number) + 5),
        pct90: Math.min(100, (prev.pct90 as number) + 5),
        pct1y: Math.min(100, (prev.pct1y as number) + 5),
        salesFirst90Total: Math.round((prev.salesFirst90Total as number) * 1.05 * 10) / 10,
        salesVetMonthly: Math.round((prev.salesVetMonthly as number) * 1.05 * 10) / 10
      }));
      setPlusFive(true);
    } else {
      // Restore saved values
      if (prePlusFiveValues) {
        setInputs(prev => ({
          ...prev,
          ...prePlusFiveValues
        }));
      }
      setPlusFive(false);
      setPrePlusFiveValues(null);
    }
  };
  const handleOptimizeForTarget = (target: number, months: 3 | 6 | 12 = timeframe) => {
    if (!target || target <= 0) return;
    const stash = inputs.monthlyCampaigns && inputs.monthlyCampaigns.length === 12 ? [...inputs.monthlyCampaigns] : Array(12).fill(inputs.campaigns);
    const pattern = optimizeCampaignsForRevenueTarget(inputs, target, months);
    if (pattern) {
      setPreOptimizeMonthlyCampaigns(stash);
      setInputs({
        ...inputs,
        monthlyCampaigns: pattern,
        campaigns: pattern[0],
        revenueTarget: target
      });
    }
  };
  const handleOptimizeForTeamTarget = (target: number, months: 3 | 6 | 12 = timeframe) => {
    if (!target || target <= 0) return;
    const stash = inputs.monthlyCampaigns && inputs.monthlyCampaigns.length === 12 ? [...inputs.monthlyCampaigns] : Array(12).fill(inputs.campaigns);
    const pattern = optimizeCampaignsForTeamTarget(inputs, target, months);
    if (pattern) {
      setPreOptimizeMonthlyCampaigns(stash);
      setInputs({
        ...inputs,
        monthlyCampaigns: pattern,
        campaigns: pattern[0]
      });
    }
  };
  const handleUndoOptimize = () => {
    if (preOptimizeMonthlyCampaigns) {
      setInputs({
        ...inputs,
        monthlyCampaigns: preOptimizeMonthlyCampaigns,
        campaigns: preOptimizeMonthlyCampaigns[0]
      });
      setPreOptimizeMonthlyCampaigns(null);
    }
  };
  // Handle numeric input changes for Review section
  const handleNumericChange = (field: keyof Inputs, value: string) => {
    if (value === '') {
      setInputs({
        ...inputs,
        [field]: ''
      });
      return;
    }
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setInputs({
        ...inputs,
        [field]: numValue
      });
    }
  };
  const setMonthlyAdSpendTotal = (total: number) => {
    // Any custom total is allowed. The campaign tier is derived from $2,500
    // bands: $1-2,500 = 1x, $2,501-5,000 = 2x, $5,001-7,500 = 3x, and so on.
    const normalizedTotal = Math.max(1, Math.round(total));
    const campaignCount = Math.ceil(normalizedTotal / CAMPAIGN_SPEND_STEP);
    const spendPerCampaign = normalizedTotal / campaignCount;
    setReviewSpendInput('');
    setInputs(currentInputs => ({
      ...currentInputs,
      campaigns: campaignCount,
      monthlyCampaigns: Array(12).fill(campaignCount),
      adSpend: spendPerCampaign,
      monthlyAdSpend: Array(12).fill(spendPerCampaign)
    }));
  };
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopyLinkLabel('Link Copied✓');
      setTimeout(() => setCopyLinkLabel('Copy Share Link'), 1200);
    } catch (err) {
      console.error('Failed to copy link', err);
    }
  };
  const handleSavePdf = async () => {
    setSavePdfLabel('Generating PDF...');
    try {
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;
      const element = resultsRef.current;
      if (!element) return;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = canvas.height * imgWidth / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      const companyName = inputs.companyName || 'Company';
      const fileName = `${companyName.replace(/\s+/g, '_')}_ROI_Projection.pdf`;
      pdf.save(fileName);
      setSavePdfLabel('PDF Saved ✓');
      setTimeout(() => setSavePdfLabel('Save as PDF'), 2000);
    } catch (err) {
      console.error('Failed to generate PDF', err);
      setSavePdfLabel('Failed - Try Again');
      setTimeout(() => setSavePdfLabel('Save as PDF'), 2000);
    }
  };
  // Calculated values - REVERSED RETENTION FUNNEL
  // Start with 1 rep at Active 1 Year and calculate upward
  const targetActive1Year = 1; // Fixed: 1 rep who stays long-term
  // Work backwards through retention rates to find how many hires needed
  const reverseActive90 = targetActive1Year / (inputs.pct1y / 100);
  const reverseActive30 = reverseActive90 / (inputs.pct90 / 100);
  const reverseLaunched = reverseActive30 / (inputs.pct30 / 100);
  const reverseHires = reverseLaunched / (inputs.pctLaunched / 100);
  // Keep the original forward calculations for other uses
  const adSpendTotal = inputs.adSpend * inputs.campaigns;
  const estimatedHires = Math.round(adSpendTotal / inputs.cph);
  // The cost-per-hire band around the median that drives the projections. Each
  // end can be edited directly; the median is always the midpoint.
  const cphRange = {
    low: inputs.cphLow ?? Math.max(1, inputs.cph - CPH_RANGE_SPREAD),
    high: inputs.cphHigh ?? inputs.cph + CPH_RANGE_SPREAD
  };
  const setCphRange = (low: number, high: number) => {
    const safeLow = Math.max(1, Math.round(low));
    const safeHigh = Math.max(safeLow, Math.round(high));
    setInputs(prev => ({
      ...prev,
      cphLow: safeLow,
      cphHigh: safeHigh,
      cph: Math.round((safeLow + safeHigh) / 2)
    }));
  };
  // While typing, the raw text is held locally so the field can be cleared and
  // retyped without being clamped on every keystroke.
  const commitCphDrafts = () => {
    const parsedLow = parseFloat(cphLowDraft ?? '');
    const parsedHigh = parseFloat(cphHighDraft ?? '');
    const nextLow = Number.isNaN(parsedLow) ? cphRange.low : parsedLow;
    const nextHigh = Number.isNaN(parsedHigh) ? cphRange.high : parsedHigh;
    setCphLowDraft(null);
    setCphHighDraft(null);
    setCphRange(nextLow, nextHigh);
  };
  const shiftCphRange = (delta: number) => {
    if (delta < 0 && cphRange.low + delta < 1) return;
    setCphRange(cphRange.low + delta, cphRange.high + delta);
  };
  // Hires at the top of the cost band (fewest) through the bottom (most).
  const hiresRange = {
    low: Math.round(adSpendTotal / cphRange.high),
    high: Math.round(adSpendTotal / cphRange.low)
  };
  const estimatedLaunched = estimatedHires * (inputs.pctLaunched / 100);
  const estimatedActive30 = estimatedLaunched * (inputs.pct30 / 100);
  const estimatedActive90 = estimatedActive30 * (inputs.pct90 / 100);
  const estimatedActive1Year = estimatedActive90 * (inputs.pct1y / 100);
  const gpPerSale = inputs.ticket * (inputs.gpm / 100);
  const rookieTotalSales = inputs.salesFirst90Total;
  // Round rep counts to nearest tenth for clean display
  const roundedActive90 = Math.round(estimatedActive90 * 10) / 10;
  const roundedActive1Year = Math.round(estimatedActive1Year * 10) / 10;
  const rookieMonthlyRevenue = roundedActive90 * rookieTotalSales * inputs.ticket * (inputs.pullthrough / 100) / 3;
  const veteranMonthlyRevenue = roundedActive1Year * inputs.salesVetMonthly * inputs.ticket * (inputs.pullthrough / 100);
  const canCalculate = true; // Allow calculation even without package selected
  // Show loading during initial mount to prevent hydration mismatch
  if (!isClient) {
    return <div className="d2d-theme min-h-screen" data-id="element-558" data-mp="7pobdkafkjxx"></div>;
  }
  // If not authenticated, show login screen
  if (!isAuthenticated) {
    return <div className="d2d-theme min-h-screen flex items-center justify-center p-4" data-id="element-559" data-mp="bsb3lmb0k6vn">
        <div className="w-full max-w-md" data-id="element-560" data-mp="1j5lb83jt4o9x">
          <div className="text-center mb-8" data-id="element-561" data-mp="1j4k0y1am85t9">
            <img src="https://cdn.magicpatterns.com/uploads/agUMR24bt7hzCEPWvymd5Y/Full_Multi_color.png" alt="HIRE" className="h-12 mx-auto mb-6" data-id="element-562" data-mp="1n5vqk6thor1g" />
            <h1 className="text-2xl font-bold text-navy mb-2" data-id="element-563" data-mp="1gqoh5aquz8s0">
              ROI Calculator Access
            </h1>
            <p className="text-gray-600" data-id="element-564" data-mp="1vogwa1shtv5o">Enter your access code to continue</p>
          </div>

          <form onSubmit={handleLogin} className="bg-white border-2 border-gray-200 rounded-lg p-8 shadow-lg" data-id="element-565" data-mp="1qo2wf711priq">
            <div className="mb-6" data-id="element-566" data-mp="24m0z3f607zie">
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-567" data-mp="1zpkeguy0k3a2">
                Access Code
              </label>
              <input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full p-3 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors font-mono" placeholder="Enter code" autoFocus data-id="element-568" data-mp="29eamoojncnhi" />
              {error && <p className="mt-2 text-sm text-red-600" data-id="element-569" data-mp="36zc2qx753cti">{error}</p>}
            </div>

            <button type="submit" className="w-full py-3 rounded bg-amber hover:bg-amber-dark text-navy font-bold text-base transition-colors shadow-md" data-id="element-570" data-mp="2t1f5dykv32ds">
              Access Calculator
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-6" data-id="element-571" data-mp="2o1j261jzneza">
            Your access will be saved for future visits
          </p>
        </div>
      </div>;
  }
  // Main calculator content
  return <div className={`d2d-theme min-h-screen ${isLightMode ? 'light-mode' : ''}`} data-id="element-572" data-mp="2ebpu3aj21hzs">
      {/* Floating Edit Button - Always visible */}
      <button onClick={() => setIsSidebarOpen(true)} className="fixed bottom-6 right-6 z-30 w-14 h-14 rounded-full bg-amber hover:bg-amber-dark text-navy shadow-2xl flex items-center justify-center transition-all hover:scale-110 active:scale-95" title="Edit Assumptions" data-id="element-573" data-mp="20d9l1cxpzfeh">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-574" data-mp="62jluskhnauj">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" data-id="element-575" data-mp="ttoyyzz1np15" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" data-id="element-576" data-mp="trkwmmgsmy2z" />
        </svg>
      </button>

      {/* Assumptions Sidebar */}
      <AssumptionsSidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} inputs={inputs} onChange={setInputs} data-id="element-577" data-mp="kshcdf5w4cmw" />

      {/* Header */}
      <header className="border-b border-white/10 bg-navy/95" data-id="element-578" data-mp="35iyfehxv2tvu">
        <div className="max-w-[1440px] mx-auto px-5 md:px-8 py-4 flex items-center justify-between gap-6" data-id="element-579" data-mp="35h542pcazlhi">
          <div className="flex items-center gap-4 shrink-0" data-id="element-580" data-mp="30mhppjb0k7gn">
            <div className={`rounded-md px-3 py-2 shadow-sm ${isLightMode ? 'bg-white' : 'bg-navy'}`} aria-label="HIRE" data-id="element-581" data-mp="2qhj4n3sw91ss">
              <img src={isLightMode ? 'https://cdn.magicpatterns.com/uploads/agUMR24bt7hzCEPWvymd5Y/Full_Multi_color.png' : 'https://cdn.magicpatterns.com/uploads/dakpUsyM83GcxWqCq39SEW/HIRE_White_Logo.png'} alt="HIRE" className="h-7 md:h-8" data-id="element-582" data-mp="30entjbe4jbda" />
            </div>
            <span className="hidden sm:block pl-4 border-l border-white/15 text-lg md:text-2xl font-bold tracking-[-0.02em] text-white" data-id="element-583" data-mp="3j55a6hlomgia">
              High Impact Recruiting Engine
            </span>
          </div>
          <div className="flex items-center gap-3 shrink-0" data-id="element-584" data-mp="1ormg5hqv1g1q">
            <span className="hidden lg:flex items-center gap-2 text-xs font-semibold text-slate-300" data-id="element-585" data-mp="1ot7bh9l0130n">
              <span className="w-1.5 h-1.5 rounded-full bg-amber" data-id="element-586" data-mp="3vnholva64lfb" />
              Built for D2D teams
            </span>
            <button type="button" onClick={() => setIsLightMode(currentMode => !currentMode)} className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-white/10 px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber focus-visible:ring-offset-2 focus-visible:ring-offset-navy" aria-pressed={isLightMode} aria-label={isLightMode ? 'Switch to dark mode' : 'Switch to light mode'} data-id="element-587" data-mp="3qownltn3dxpt">
              <span aria-hidden="true" className="text-sm leading-none" data-id="element-588" data-mp="3lmywyecd40mu">
                {isLightMode ? '◐' : '☀'}
              </span>
              {isLightMode ? 'Dark mode' : 'Light mode'}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto px-5 md:px-8 py-10 md:py-16 pb-20" data-id="element-589" data-mp="wwfy3j1c7k6j">
        {/* Hero Section */}
        <div className="mb-12 md:mb-16 text-center max-w-4xl mx-auto" data-id="element-590" data-mp="14ffd7chrmpk3">
          <h1 className="hero-title text-4xl md:text-5xl lg:text-6xl font-medium text-white leading-[1.08] tracking-[-0.045em]" data-id="element-591" data-mp="19gd4cekr6txq">
            Predictable recruiting,
            <br data-id="element-592" data-mp="zhxd8blq7ko8" />
            profitable growth.
          </h1>
          <div className="inline-flex items-center gap-2 mt-5 text-[11px] font-extrabold tracking-[0.16em] uppercase text-amber" data-id="element-593" data-mp="zifgvtapx166">
            <span className="w-1.5 h-1.5 rounded-full bg-amber" data-id="element-594" data-mp="1n9uex9vgowbm" />
            Recruiting forecast for D2D sales leaders
          </div>
        </div>

        {/* Step 1: Input Form */}
        <div className="mb-8 md:mb-12" data-id="element-595" data-mp="1dflznnvfyzve">
          <div className="flex items-center gap-3 mb-4 md:mb-6" data-id="element-596" data-mp="1ig9mdxcn0ujt">
            <div className="w-10 h-10 rounded bg-amber flex items-center justify-center text-lg font-bold text-navy" data-id="element-597" data-mp="1ief1uk5a34g9">
              1
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-navy" data-id="element-598" data-mp="2ay44mmvsq481">
              Business Assumptions
            </h2>
          </div>
          <CalculatorInputs inputs={inputs} onChange={setInputs} onReset={handleReset} onReview={handleReview} showActionButtons={false} data-id="element-599" data-mp="3cv0ipfsc4qef" />
        </div>

        {/* Step 2: Validation Summary */}
        {hasReviewed && <div ref={reviewRef} className="mb-8 md:mb-12" data-id="element-600" data-mp="3rl7oow15ogy8">
            <div className="flex items-center gap-3 mb-4 md:mb-6" data-id="element-601" data-mp="2tz0pk1nmfgt9">
              <div className="w-10 h-10 rounded bg-amber flex items-center justify-center text-lg font-bold text-navy" data-id="element-602" data-mp="2yxby8926jl9w">
                2
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-navy" data-id="element-603" data-mp="33utxt8ub4o58">
                Review Your Numbers
              </h2>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-5 md:p-7 shadow-lg" data-id="element-604" data-mp="j663rs0f937g">
              {/* Hiring Funnel Breakdown Button */}
              <div className="mb-6 flex justify-end gap-3" data-id="element-605" data-mp="o3xvom12gpbn">
                <button onClick={handlePlusFive} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${plusFive ? 'bg-blue-500 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-600'} active:translate-y-[1px]`} data-id="element-606" data-mp="28n1nxm8ztgyn">
                  {plusFive ? '✓ ' : ''}+5%
                </button>
                <button onClick={handleToggleOnTarget} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${onTarget ? 'bg-emerald-500 text-white border-emerald-600' : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-400'}`} data-id="element-607" data-mp="1ozkirymze0dt">
                  {onTarget ? '✓ ' : ''}On Target
                </button>
              </div>

              {/* Monthly Ad Investment & Retention Funnel - Side by Side */}
              <div className="mb-8 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6" data-id="element-608" data-mp="35d2dvol3m3hj">
                {/* Left: Monthly Ad Investment */}
                <div data-id="element-609" data-mp="13mts6r99h11w"></div>

                {/* Right: Retention Funnel Visualization */}
              </div>

              {/* Expandable Hiring Funnel - Conditionally shown */}
              <div className="mb-4" data-id="element-610" data-mp="tr9v41iynwbk">
                {/* Overhead Section - appears ABOVE Recruiting Metrics */}
                {showOverhead && <div className="mb-8" data-id="element-611" data-mp="ymyxecmd7rbk">
                    <div className="relative group" data-id="element-612" data-mp="1memgi8ddxy3a">
                      <h4 className="text-lg font-bold text-navy mb-4 border-b border-gray-200 pb-2" data-id="element-613" data-mp="1md1qmw20n3br">
                        Overhead
                        <button onClick={e => {
                    e.stopPropagation();
                    setShowOverhead(false);
                  }} className="ml-3 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-xs font-bold text-gray-400 hover:text-amber-500 uppercase tracking-wide align-middle" aria-label="Collapse Overhead" data-id="element-614" data-mp="1chhuq9ngc4n0">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-615" data-mp="2f8j0dz1rdps9">
                            <polyline points="6 9 12 15 18 9" data-id="element-616" data-mp="1wknu108h7e96" />
                          </svg>
                          Collapse
                        </button>
                      </h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-617" data-mp="25hemz3rqeczj">
                      {/* Total Time Investment */}
                      <div className="p-4 rounded bg-orange-50 border-2 border-orange-200" data-id="element-618" data-mp="1dsl5z3ti8vow">
                        <div className="text-sm font-bold text-navy mb-3" data-id="element-619" data-mp="1qhuabdjrfpou">
                          Total Time Investment
                        </div>
                        <div className="space-y-3" data-id="element-620" data-mp="1vh6ufqj8yinq">
                          <div className="cursor-pointer hover:bg-orange-100 p-2 rounded transition-colors" data-id="element-621" data-mp="20g07xiyja9nw">
                            <div className="text-xs text-gray-600 mb-1 font-semibold uppercase tracking-wide" data-id="element-622" data-mp="256qg13ycgqma">
                              Pre-Interview
                            </div>
                            <div className="relative" data-id="element-623" data-mp="2e6lwnoao5dtp">
                              <input type="number" min="0" step="1" value={preInterviewHours} onChange={e => setPreInterviewHours(parseFloat(e.target.value) || 0)} className="w-full text-xl font-bold text-navy bg-transparent border-none outline-none focus:text-amber transition-colors" data-id="element-624" data-mp="2e5bxevs2xzlh" />
                              <span className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-sm" data-id="element-625" data-mp="ch69j4nmzul4">
                                hours
                              </span>
                            </div>
                          </div>
                          <div className="cursor-pointer hover:bg-orange-100 p-2 rounded transition-colors" data-id="element-626" data-mp="154akdq9ev34t">
                            <div className="text-xs text-gray-600 mb-1 font-semibold uppercase tracking-wide" data-id="element-627" data-mp="v8gw59mbxl77">
                              Interview-to-Onboarding
                            </div>
                            <div className="relative" data-id="element-628" data-mp="3fyoi5eb8h5ml">
                              <input type="number" min="0" step="1" value={interviewToOnboardingHours} onChange={e => setInterviewToOnboardingHours(parseFloat(e.target.value) || 0)} className="w-full text-xl font-bold text-navy bg-transparent border-none outline-none focus:text-amber transition-colors" data-id="element-629" data-mp="3g09b4zhwggic" />
                              <span className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-sm" data-id="element-630" data-mp="1o35a26onj2kc">
                                hours
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Money Value of Time */}
                      <div className="p-4 rounded bg-red-50 border-2 border-red-200" data-id="element-631" data-mp="15ce4zhbp5ybr">
                        <div className="text-sm font-bold text-navy mb-3" data-id="element-632" data-mp="15e8fs2t98a24">
                          Money Value of Time
                        </div>
                        <div className="space-y-3" data-id="element-633" data-mp="hq260z8a0pey">
                          <div className="cursor-pointer hover:bg-red-100 p-2 rounded transition-colors" data-id="element-634" data-mp="hrd9exxt7v5j">
                            <div className="text-xs text-gray-600 mb-1 font-semibold uppercase tracking-wide" data-id="element-635" data-mp="qpoxas7akeua">
                              Admin
                            </div>
                            <div className="relative" data-id="element-636" data-mp="14ndg7uhstbwp">
                              <span className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-lg" data-id="element-637" data-mp="10mtlsyom0me9">
                                $
                              </span>
                              <input type="text" value={adminCost.toLocaleString()} onChange={e => {
                          const numericValue = e.target.value.replace(/,/g, '');
                          setAdminCost(parseFloat(numericValue) || 0);
                        }} className="w-full pl-5 text-xl font-bold text-navy bg-transparent border-none outline-none focus:text-amber transition-colors" data-id="element-638" data-mp="19fwuktob8b2k" />
                            </div>
                          </div>
                          <div className="cursor-pointer hover:bg-red-100 p-2 rounded transition-colors" data-id="element-639" data-mp="3mg129e6t38op">
                            <div className="text-xs text-gray-600 mb-1 font-semibold uppercase tracking-wide" data-id="element-640" data-mp="3rfdzmsvazy5j">
                              Leadership
                            </div>
                            <div className="relative" data-id="element-641" data-mp="29mz2an09d9">
                              <span className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-lg" data-id="element-642" data-mp="2ypnkqhluks8d">
                                $
                              </span>
                              <input type="text" value={leadershipCost.toLocaleString()} onChange={e => {
                          const numericValue = e.target.value.replace(/,/g, '');
                          setLeadershipCost(parseFloat(numericValue) || 0);
                        }} className="w-full pl-5 text-xl font-bold text-navy bg-transparent border-none outline-none focus:text-amber transition-colors" data-id="element-643" data-mp="1ktdqi300176x" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>}

                <div className="relative group" data-id="element-644" data-mp="3gxr7m365qutk">
                  {/* Hidden arrow above Recruiting Metrics heading - shows when funnel is fully expanded */}
                  {showHiringMetrics && !showOverhead && <button onClick={() => setShowOverhead(true)} className="absolute -top-6 left-0 w-full flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer z-10" aria-label="Show Overhead" data-id="element-645" data-mp="379995slcozt3">
                      <div className="w-20 text-right flex justify-end pr-4" data-id="element-646" data-mp="3uyldof5qgjp2">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-bounce" data-id="element-647" data-mp="3pxe6ah7i5mix">
                          <polyline points="18 15 12 9 6 15" data-id="element-648" data-mp="u1hrqlpk0yn9" />
                        </svg>
                      </div>
                      <div className="text-xs font-bold text-amber-500 uppercase tracking-wide" data-id="element-649" data-mp="z02q0fl8x892">
                        Click to expand
                      </div>
                    </button>}

                  <h3 className="text-lg font-bold text-navy mb-4 border-b border-gray-200 pb-2" data-id="element-650" data-mp="18pcivxnwyuy8">
                    Recruiting Metrics
                    {/* Collapse button for both Overhead + Recruiting Metrics */}
                    {showHiringMetrics && <button onClick={e => {
                  e.stopPropagation();
                  setShowHiringMetrics(false);
                  setShowOverhead(false);
                }} className="ml-3 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-xs font-bold text-gray-400 hover:text-amber-500 uppercase tracking-wide align-middle" aria-label="Collapse Recruiting Metrics" data-id="element-651" data-mp="1mq6x4wxn7q4t">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-652" data-mp="14p1f1c9ndfb1">
                          <polyline points="6 9 12 15 18 9" data-id="element-653" data-mp="1ndg61zjystbu" />
                        </svg>
                        Collapse
                      </button>}
                  </h3>
                </div>

                {showHiringMetrics && <div className="mb-6" data-id="element-654" data-mp="1sedjjyu14z70">
                    <ExpandableHiringFunnel estimatedHires={reverseHires} data-id="element-655" data-mp="1sb7uukpbbzbt" />
                  </div>}
              </div>

              {/* Projected Recruiting Metrics */}
              <div className="mb-8" data-id="element-656" data-mp="2gxfm3p5pv2kt">
                <div className="relative group" data-id="element-657" data-mp="2216ost5dyzb1">
                  {/* Hidden arrow above Retention Metrics heading */}
                  {!showHiringMetrics && <button onClick={() => setShowHiringMetrics(true)} className="absolute -top-6 left-0 w-full flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer z-10" aria-label="Show Recruiting Metrics" data-id="element-658" data-mp="26y6lidgcipph">
                      <div className="w-20 text-right flex justify-end pr-4" data-id="element-659" data-mp="1o9091865auc4">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-bounce" data-id="element-660" data-mp="1a9qqi1ugvo4z">
                          <polyline points="18 15 12 9 6 15" data-id="element-661" data-mp="xh1cmhko8zky" />
                        </svg>
                      </div>
                      <div className="text-xs font-bold text-amber-500 uppercase tracking-wide" data-id="element-662" data-mp="sizo3h4h8823">
                        Recruiting Metrics
                      </div>
                    </button>}

                  <h3 className="text-lg font-bold text-navy mb-4 border-b border-gray-200 pb-2" data-id="element-663" data-mp="jkxnh2onnhfc">
                    Retention Metrics
                  </h3>
                </div>

                <div className="space-y-3" data-id="element-664" data-mp="9m84f6l0vqxi">
                  <div className="flex items-center gap-3" data-id="element-665" data-mp="28y16viqkxohm">
                    <div className="w-20 text-right" data-id="element-666" data-mp="28vexreo1j0xa">
                      <span className="text-xl font-bold text-navy" data-id="element-667" data-mp="23zhjddwc84n5">
                        {reverseHires.toFixed(1)}
                      </span>
                    </div>
                    <div className="w-28 text-base font-semibold text-gray-700" data-id="element-668" data-mp="2ggsczve0a8p9">
                      Hired
                    </div>
                    <div className="flex-1 bg-white/10 rounded-full h-8 relative overflow-hidden border border-white/10" data-id="element-669" data-mp="2l91waskm8aik">
                      <div className="absolute inset-y-0 left-0 bg-amber rounded-full" style={{
                    width: '100%'
                  }} data-id="element-670" data-mp="2q621z2djzb95" />
                    </div>
                  </div>

                  <div className="flex items-center gap-3" data-id="element-671" data-mp="22fo8r8j8f6cz">
                    <div className="w-20 text-right" data-id="element-672" data-mp="27gvgx8pae7k6">
                      <span className="text-lg font-bold text-navy" data-id="element-673" data-mp="27hnakdzbpv7t">
                        {reverseLaunched.toFixed(1)}
                      </span>
                    </div>
                    <div className="w-28 text-base font-semibold text-gray-700" data-id="element-674" data-mp="3iu7mbo0p2y8g">
                      Launched
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden" data-id="element-675" data-mp="2t96n2m9aq41p">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-amber to-amber/90 rounded-full" style={{
                    width: `${reverseLaunched / reverseHires * 100}%`
                  }} data-id="element-676" data-mp="2oamva580ty1f" />
                    </div>
                    <div className="w-16 text-sm font-semibold text-gray-600" data-id="element-677" data-mp="25iavnq8r5auz">
                      {inputs.pctLaunched}%
                    </div>
                  </div>

                  <div className="flex items-center gap-3" data-id="element-678" data-mp="2ffytip1nmyhd">
                    <div className="w-20 text-right" data-id="element-679" data-mp="2fedzd0j95hk1">
                      <span className="text-lg font-bold text-navy" data-id="element-680" data-mp="1rq4eq1xryzuh">
                        {reverseActive30.toFixed(1)}
                      </span>
                    </div>
                    <div className="w-28 text-base font-semibold text-gray-700" data-id="element-681" data-mp="1mpgq1mx0qv3f">
                      Active 30d
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden" data-id="element-682" data-mp="708rve28j66b">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-blue-400 rounded-full" style={{
                    width: `${reverseActive30 / reverseHires * 100}%`
                  }} data-id="element-683" data-mp="3oh8lds00babv" />
                    </div>
                    <div className="w-16 text-sm font-semibold text-gray-600" data-id="element-684" data-mp="py1wqlwf5q6c">
                      {inputs.pct30}%
                    </div>
                  </div>

                  <div className="flex items-center gap-3" data-id="element-685" data-mp="fzm58hthcewb">
                    <div className="w-20 text-right" data-id="element-686" data-mp="2wpase8i2pt00">
                      <span className="text-lg font-bold text-navy" data-id="element-687" data-mp="2wohrvp9pjzqb">
                        {reverseActive90.toFixed(1)}
                      </span>
                    </div>
                    <div className="w-28 text-base font-semibold text-gray-700" data-id="element-688" data-mp="3sepmlk0a7dnl">
                      Active 90d
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden" data-id="element-689" data-mp="3ne1n09xzlohr">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-500 to-green-400 rounded-full" style={{
                    width: `${reverseActive90 / reverseHires * 100}%`
                  }} data-id="element-690" data-mp="3je18kpaw1dap" />
                    </div>
                    <div className="w-16 text-sm font-semibold text-gray-600" data-id="element-691" data-mp="66zhvc7ekcff">
                      {inputs.pct90}%
                    </div>
                  </div>

                  <div className="flex items-center gap-3" data-id="element-692" data-mp="1622169v7uh3">
                    <div className="w-20 text-right" data-id="element-693" data-mp="yollo5esi8qc">
                      <span className="text-lg font-bold text-navy" data-id="element-694" data-mp="ypd4efnagbz7">
                        {targetActive1Year.toFixed(1)}
                      </span>
                    </div>
                    <div className="w-28 text-base font-semibold text-gray-700" data-id="element-695" data-mp="119t4ytrgd7uf">
                      Active 1 Year
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden" data-id="element-696" data-mp="1b5mo4vzofzl6">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-purple-500 to-purple-400 rounded-full" style={{
                    width: `${targetActive1Year / reverseHires * 100}%`
                  }} data-id="element-697" data-mp="1f897s9m72e7a" />
                    </div>
                    <div className="w-16 text-sm font-semibold text-gray-600" data-id="element-698" data-mp="1tx5qd0lvn9m5">
                      {inputs.pct1y}%
                    </div>
                  </div>
                </div>
              </div>

              {/* Sales Performance and Deal Economics - Side by Side */}
              <div className="mb-8" data-id="element-699" data-mp="27xgf9y3mme4o">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-700" data-mp="27uc6soj4ow87">
                  {/* Left Column: Monthly Ad Spend */}
                  <div data-id="element-701" data-mp="3emw83rala1hg">
                    <div className="group relative p-4 md:p-6 rounded bg-gray-50 border-2 border-gray-200 flex flex-col justify-center transition-colors hover:border-amber" style={{
                  minHeight: '120px'
                }} data-id="element-702" data-mp="3eo79xlkh4z5x">
                      <div className="text-xs text-gray-600 mb-2 font-semibold uppercase tracking-wide" data-id="element-703" data-mp="3tdd9jan84rec">
                        Monthly Ad Spend
                      </div>
                      <div className="relative" data-id="element-704" data-mp="3jn0nfdzrgq63">
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-xl" data-id="element-705" data-mp="3jk4phpnwy1hn">
                          $
                        </span>
                        <input type="number" min={CAMPAIGN_SPEND_STEP} step={CAMPAIGN_SPEND_STEP} value={reviewSpendInput || adSpendTotal} onChange={e => setReviewSpendInput(e.target.value)} onBlur={() => {
                      if (reviewSpendInput) {
                        setMonthlyAdSpendTotal(parseFloat(reviewSpendInput));
                      }
                    }} onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur();
                      }
                    }} className="no-spinner inline-field w-full pl-6 pr-16 text-2xl md:text-3xl font-bold text-navy bg-transparent border-none outline-none focus:text-amber transition-colors" aria-label="Monthly ad spend" data-id="element-706" data-mp="g3xndnl5ccaz" />
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-200" data-id="element-707" data-mp="324sgax812g8t">
                        <button type="button" onClick={() => setMonthlyAdSpendTotal(adSpendTotal + CAMPAIGN_SPEND_STEP)} className="flex h-6 w-6 items-center justify-center rounded-full bg-navy-light text-xs font-bold text-white shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-amber" aria-label="Increase monthly ad spend by $2,500" data-id="element-708" data-mp="24pyujitfvh85">
                          ↑
                        </button>
                        {adSpendTotal > CAMPAIGN_SPEND_STEP && <button type="button" onClick={() => setMonthlyAdSpendTotal(adSpendTotal - CAMPAIGN_SPEND_STEP)} className="flex h-6 w-6 items-center justify-center rounded-full bg-navy-light text-xs font-bold text-white shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-amber" aria-label="Decrease monthly ad spend by $2,500" data-id="element-709" data-mp="xblzx4qg1hj8">
                            ↓
                          </button>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Middle Column: Average Ad Spend Cost per Hire */}
                  <div data-id="element-710" data-mp="3tamhhua4ev3z">
                    <div className="group relative p-4 md:p-6 rounded bg-blue-50 border-2 border-blue-200 flex flex-col justify-center hover:border-amber transition-colors" style={{
                  minHeight: '120px'
                }} data-id="element-711" data-mp="3tbfql1wjxwpa">
                      <div className="text-xs text-gray-600 mb-2 font-semibold uppercase tracking-wide" data-id="element-712" data-mp="1o6tju63uzirm">
                        Ad Spend Cost per Hire Range
                      </div>
                      <div className="relative" data-id="element-713" data-mp="1efp6fatu05yw">
                        <div className="flex items-center pr-16 text-2xl md:text-3xl font-bold text-navy" data-id="element-714" data-mp="1jf1vm286dp4n">
                          <span className="text-gray-500 text-xl" data-id="element-715" data-mp="14hj790zetdxq">$</span>
                          <input type="number" min="1" step={CPH_STEP} value={cphLowDraft ?? cphRange.low} onChange={e => setCphLowDraft(e.target.value)} onBlur={commitCphDrafts} onKeyDown={e => {
                        if (e.key === 'Enter') e.currentTarget.blur();
                      }} aria-label="Low end of ad spend cost per hire" style={{
                        width: `${fieldWidthCh(cphLowDraft ?? cphRange.low)}ch`
                      }} className="no-spinner inline-field text-2xl md:text-3xl font-bold text-navy bg-transparent border-none outline-none focus:text-amber transition-colors" data-id="element-716" data-mp="19h46zdmelqbi" />
                          <span className="text-gray-400 mx-1" data-id="element-717" data-mp="3nnu1jzxiykk7">&ndash;</span>
                          <span className="text-gray-500 text-xl" data-id="element-718" data-mp="3iqbnjv4nhf5c">$</span>
                          <input type="number" min="1" step={CPH_STEP} value={cphHighDraft ?? cphRange.high} onChange={e => setCphHighDraft(e.target.value)} onBlur={commitCphDrafts} onKeyDown={e => {
                        if (e.key === 'Enter') e.currentTarget.blur();
                      }} aria-label="High end of ad spend cost per hire" style={{
                        width: `${fieldWidthCh(cphHighDraft ?? cphRange.high)}ch`
                      }} className="no-spinner inline-field text-2xl md:text-3xl font-bold text-navy bg-transparent border-none outline-none focus:text-amber transition-colors" data-id="element-719" data-mp="3is6838c0f58w" />
                        </div>
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-200" data-id="element-720" data-mp="p5gpgz2646cn">
                          <button type="button" onClick={() => shiftCphRange(CPH_STEP)} className="flex h-6 w-6 items-center justify-center rounded-full bg-navy-light text-xs font-bold text-white shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-amber" aria-label="Increase cost per hire range by $50" data-id="element-721" data-mp="yz5u7pevagov">
                            ↑
                          </button>
                          {cphRange.low > CPH_STEP && <button type="button" onClick={() => shiftCphRange(-CPH_STEP)} className="flex h-6 w-6 items-center justify-center rounded-full bg-navy-light text-xs font-bold text-white shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-amber" aria-label="Decrease cost per hire range by $50" data-id="element-722" data-mp="2ehlnnhsbpn73">
                              ↓
                            </button>}
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-gray-600" data-id="element-723" data-mp="3q1r1jio3cls3">
                        Modeled at the median: ${inputs.cph.toLocaleString()}
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Expected Monthly Hires */}
                  <div data-id="element-724" data-mp="2pd80hn6flfhc">
                    <div className="expected-hires-card p-4 md:p-6 rounded-lg bg-navy border border-amber flex flex-col justify-center" style={{
                  minHeight: '120px'
                }} data-id="element-725" data-mp="2tgwb6eqd0r6t">
                      <div className="text-xs text-amber mb-2 font-extrabold uppercase tracking-wide" data-id="element-726" data-mp="3m3h8rwxfhzit">
                        Expected Monthly Hires
                      </div>
                      <div className="text-3xl md:text-4xl font-extrabold text-white" data-id="element-727" data-mp="3d3c43ujmdf8c">
                        {hiresRange.low}&ndash;{hiresRange.high}
                      </div>

                    </div>
                  </div>
                </div>
              </div>

              <button onClick={handleCalculate} className="w-full py-4 md:py-5 rounded font-bold text-base md:text-lg transition-all shadow-lg bg-amber hover:bg-amber-dark text-navy active:translate-y-[1px] cursor-pointer" data-id="element-728" data-mp="1t7y5umk03in0">
                Calculate My ROI →
              </button>
            </div>
          </div>}

        {/* Step 3: Results */}
        {hasCalculated && results && <div ref={resultsRef} className="scroll-mt-8" data-id="element-729" data-mp="1ag5fz108adji">
            <div className="flex items-center gap-3 mb-4 md:mb-6" data-id="element-730" data-mp="15k6lqws7y1b6">
              <div className="w-10 h-10 rounded bg-amber flex items-center justify-center text-lg font-bold text-navy" data-id="element-731" data-mp="1k7rnam9g3sgz">
                3
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-navy" data-id="element-732" data-mp="1fg06h3g3d141">
                Projected Investment and ROI
              </h2>
            </div>

            <div className="mb-4" data-id="element-733" data-mp="mt5pwhmealvu">
              {inputs.pkg && <div className="inline-block bg-amber-light text-amber-dark px-4 py-2 rounded text-sm font-semibold border-2 border-amber/20" data-id="element-734" data-mp="mqt7k3cq69i9">
                  {results.preset.name}
                </div>}
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 md:p-7 shadow-lg mb-6" data-id="element-735" data-mp="3p3x062qs9ce7">
              <ResultsSummary results={results} halfSales={halfSales} doubleAttrition={doubleAttrition} timeframe={timeframe} onOptimizeForTarget={handleOptimizeForTarget} onOptimizeForTeamTarget={handleOptimizeForTeamTarget} onUndoOptimize={handleUndoOptimize} canUndoOptimize={preOptimizeMonthlyCampaigns !== null} data-id="element-736" data-mp="3kcop729t1iyx" />
            </div>

            <div className="mb-6" data-id="element-737" data-mp="2do8bn6xvs5ba">
              <ProjectionTable rows={results.rows} inputs={inputs} onInputsChange={setInputs} halfSales={halfSales} setHalfSales={setHalfSales} doubleAttrition={doubleAttrition} setDoubleAttrition={handleDoubleAttritionChange} onTarget={onTarget} onToggleOnTarget={handleToggleOnTarget} plusFive={plusFive} onPlusFive={handlePlusFive} timeframe={timeframe} setTimeframe={setTimeframe} data-id="element-738" data-mp="2dpb3bh312bs9" />
            </div>

            <div className="mb-6" data-id="element-739" data-mp="5flgnse0rydq">
              <ProfitChart rows={results.rows} breakeven={results.breakeven} halfSales={halfSales} setHalfSales={setHalfSales} doubleAttrition={doubleAttrition} setDoubleAttrition={handleDoubleAttritionChange} onTarget={onTarget} onToggleOnTarget={handleToggleOnTarget} plusFive={plusFive} onPlusFive={handlePlusFive} data-id="element-740" data-mp="9i8cs5hq45gq" />
            </div>

            <div className="flex flex-wrap gap-3 mb-6" data-id="element-741" data-mp="1tf72uvc9v6w8">
              <button onClick={handleReset} className="px-3 py-2 rounded border-2 border-gray-300 text-gray-700 hover:border-gray-400 font-semibold text-xs transition-colors active:translate-y-[1px]" data-id="element-742" data-mp="1tcktqr9qgjbw">
                Start Over
              </button>
            </div>
          </div>}
      </div>

      {/* Hiring Funnel Modal */}
      <HiringFunnelModal isOpen={showHiringFunnel} onClose={() => setShowHiringFunnel(false)} estimatedHires={reverseHires} data-id="element-743" data-mp="2c4dmckkw42j3" />
    </div>;
}